import { Link } from 'react-router-dom';
import { AlertCircleIcon, ArrowRightIcon } from 'lucide-react';
import { PageHeader } from '../ui/PageHeader';
import { OfferTabs } from './OfferTabs';
import { Panel } from '../ui/Panel';
import { Field } from '../ui/Field';
import { useAdmin } from '../AdminContext';
import { skinTypes, skinTypeTags } from '../../data/concerns';
import { asSkinType } from './catalogRules';
import { inputCls } from '../ui/styles';
import { plural } from '../../core/format/text';
import type { Concern } from '../../core/types/catalog';

/**
 * Het koppelsysteem in één scherm: per huidthema welk protocol en welke
 * behandeling je adviseert. Klachtenkiezer, huidadvies en productpagina's volgen dit.
 */
export function Concerns() {
  const {
    catalog,
    index,
    putItem
  } = useAdmin();
  const set = (c: Concern, patch: Partial<Concern>) => putItem('concerns', {
    ...c,
    ...patch
  });
  return <div data-id="element-628" data-mp="1yb9umrguwr5z">
      <PageHeader title="Huidthema’s & koppelingen" description="Per klacht bepaal je hier welke producten erbij horen, welk protocol je adviseert en welke behandeling in de salon. De klachtenkiezer, het huidadvies en de productpagina’s volgen dit automatisch." data-id="element-629" data-mp="1ybjc1qnu5q02" />
      <OfferTabs data-id="element-630" data-mp="215qv768bunvk" />

      <p className="mb-6 flex flex-wrap items-center gap-2 rounded-lg border border-line bg-white px-5 py-4 text-[13px] text-muted" data-id="element-631" data-mp="2157o6kk7tjwb">
        <span className="text-ink" data-id="element-632" data-mp="26427dnm3hagy">Product</span>
        <ArrowRightIcon className="h-3.5 w-3.5" strokeWidth={1.5} aria-label="naar" data-id="element-633" data-mp="18h23tyx2pwx0" />
        <span data-id="element-634" data-mp="1dfwlgxjb1ffj">huidthema-tag</span>
        <ArrowRightIcon className="h-3.5 w-3.5" strokeWidth={1.5} aria-label="naar" data-id="element-635" data-mp="1df3j08rdpc37" />
        <span className="text-ink" data-id="element-636" data-mp="1ieg6mvq2qn6u">Klacht</span>
        <ArrowRightIcon className="h-3.5 w-3.5" strokeWidth={1.5} aria-label="naar" data-id="element-637" data-mp="1idooamlhmlyi" />
        <span data-id="element-638" data-mp="1n79cxo16y3l1">protocol</span>
        <span className="text-muted" aria-hidden="true" data-id="element-639" data-mp="1n7j4hg1vwisu">+</span>
        <span className="sr-only" data-id="element-640" data-mp="ppj1zvescowk">en</span>
        <span data-id="element-641" data-mp="upf5zunhggc4">behandeling</span>
      </p>

      <div className="space-y-4" data-id="element-642" data-mp="uodjmu10o7f3">
        {catalog.concerns.map(c => {
        const products = index.productsForSkinType(c.skinType);
        const routine = index.routineById(c.routineId);
        const matches = routine ? index.routineTotals(routine).items.some(p => p.skinTypes.includes(c.skinType)) : false;
        return <Panel key={c.id} data-id="element-643" data-mp="wglpdbo0s6ru">
              <div className="grid gap-5 lg:grid-cols-12" data-id="element-644" data-mp="rewa1jtrl6xy">
                <div className="space-y-4 lg:col-span-5" data-id="element-645" data-mp="rhis4dea74uf">
                  <Field label="Klacht (zoals de klant hem kiest)" data-id="element-646" data-mp="3qwkgn86vmky">
                    <input value={c.label} onChange={e => set(c, {
                  label: e.target.value
                })} className={inputCls} data-id="element-647" data-mp="3uwef5ji5jzuo" />
                  </Field>
                  <Field label="Uitleg" data-id="element-648" data-mp="dossv79qs3fd">
                    <textarea value={c.description} onChange={e => set(c, {
                  description: e.target.value
                })} rows={3} className={`${inputCls} resize-y leading-relaxed`} data-id="element-649" data-mp="8oy2d4tvpfuu" />
                  </Field>
                </div>
                <div className="grid gap-4 sm:grid-cols-3 lg:col-span-7 lg:grid-cols-1 xl:grid-cols-3" data-id="element-650" data-mp="1k4d74usbxc0i">
                  <Field label="Huidthema-tag" hint={`In WooCommerce: “${skinTypeTags[c.skinType].wooCategory}”`} data-id="element-651" data-mp="1rn305bqu2udn">
                    <select value={c.skinType} onChange={e => {
                  const skinType = asSkinType(e.target.value);
                  if (skinType) set(c, {
                    skinType
                  });
                }} className={inputCls} data-id="element-652" data-mp="1wnqplszez35r">
                      {skinTypes.map(s => <option key={s} data-id="element-653" data-mp="2fcxa9ljrq4u7">{s}</option>)}
                    </select>
                    <Link to={`/be/producten?thema=${encodeURIComponent(c.skinType)}`} className="mt-1.5 inline-block text-[12px] text-clay underline-offset-2 hover:underline" data-id="element-654" data-mp="21mbtc2dvk4yy">
                      {plural(products.length, 'product', 'producten')} met deze tag
                    </Link>
                  </Field>
                  <Field label="Adviseer protocol" data-id="element-655" data-mp="1afd8ulid80tp">
                    <select value={c.routineId} onChange={e => set(c, {
                  routineId: e.target.value
                })} className={inputCls} data-id="element-656" data-mp="1ag68l2iwpq2c">
                      {catalog.routines.map(r => <option key={r.id} value={r.id} data-id="element-657" data-mp="10i8i08dkrh6k">
                          {r.name}
                        </option>)}
                    </select>
                    {!matches && <span className="mt-1.5 flex items-start gap-1 text-[12px] leading-snug text-clay" data-id="element-658" data-mp="1t4jjrmcx3o4y">
                        <AlertCircleIcon className="mt-0.5 h-3 w-3 shrink-0" strokeWidth={2} aria-hidden="true" data-id="element-659" data-mp="1o7b884m9yico" />
                        Dit protocol bevat geen product met deze tag
                      </span>}
                  </Field>
                  <Field label="Behandeling in de salon" data-id="element-660" data-mp="2bwvuoxhmzxqp">
                    <select value={c.treatmentSlug} onChange={e => set(c, {
                  treatmentSlug: e.target.value
                })} className={inputCls} data-id="element-661" data-mp="2bygo2js7tamz">
                      {catalog.treatments.map(t => <option key={t.slug} value={t.slug} data-id="element-662" data-mp="3pu878vmqscwg">
                          {t.name}
                        </option>)}
                    </select>
                  </Field>
                </div>
              </div>
            </Panel>;
      })}
      </div>
    </div>;
}